import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { sidebarRouteConfig } from '../config/sidebarRoutes';
// import EmailCapture from './EmailCapture';
// import { activeEmailConfig } from '../config/emailConfig';

const handleSmoothScroll = (e: React.MouseEvent, targetId: string) => {
    e.preventDefault();
    const element = document.getElementById(targetId);
    if (element) {
        const headerOffset = 156;
        const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
        const offsetPosition = elementPosition - headerOffset;
        
        window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
        });
        window.history.replaceState(null, '', `#${targetId}`);
    }
};

const Sidebar: React.FC = () => {
    const location = useLocation();
    // Normalize pathname to handle trailing slashes
    const pathname = location.pathname.replace(/\/$/, '') || '/';
    const config = sidebarRouteConfig[pathname] || sidebarRouteConfig['/'];
    const [activeSection, setActiveSection] = useState<string | null>(null);
    const [forceActive, setForceActive] = useState(false);
    // const [showEmailCapture, setShowEmailCapture] = useState(false);

    useEffect(() => {
        const sections = config.sections || [];

        const sectionElements = sections.map(id => document.getElementById(id)).filter(Boolean) as HTMLElement[];
        
        const handleScroll = () => {
            if (forceActive) return;
            
            const scrollPosition = window.scrollY + window.innerHeight / 2;
            const active = sectionElements.reduce((closest, section) => {
                const box = section.getBoundingClientRect();
                const sectionTop = box.top + window.pageYOffset;
                const sectionBottom = sectionTop + section.offsetHeight;
                
                if (scrollPosition >= sectionTop && scrollPosition <= sectionBottom) {
                    if (!closest || section.offsetHeight > closest.offsetHeight) {
                        return section;
                    }
                }
                return closest;
            }, null as HTMLElement | null);

            if (active) setActiveSection(active.id);
        };

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting && !forceActive) {
                        setActiveSection(entry.target.id);
                    }
                });
            },
            { 
                rootMargin: '-40% 0px -40% 0px',
                threshold: 0.2
            }
        );

        sectionElements.forEach(element => observer.observe(element));
        window.addEventListener('scroll', handleScroll);
        handleScroll();

        return () => {
            observer.disconnect();
            window.removeEventListener('scroll', handleScroll);
        };
    }, [config.sections, forceActive]);

    const handleClick = (section: string) => {
        setForceActive(true);
        setActiveSection(section);
        setTimeout(() => setForceActive(false), 1000);
    };

    const getLinkClass = (sectionId: string) => 
        `sidebar-item text-gray-400 hover:text-black transition-colors duration-500 
        ${activeSection === sectionId ? '!text-black' : ''}`;

    return (
        <nav className="h-screen bg-white p-4 md:px-6 flex flex-col z-0">
            {/* Fixed Header */}
            <div className="sticky top-0 bg-white pb-0 z-10">
                <div className="text-right">
                    <Link to="/" className="text-black bg-white md:bg-transparent px-2 py-1 rounded-md">
                        Taylor Pemberton
                    </Link>
                </div>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto pt-14">
                <div className="text-right">
                </div>
                <div className="flex flex-col text-right">
                    {config.sections ? (
                        // Render section links (for About and Home pages)
                        config.sections.map((sectionId) => (
                            <Link
                                key={sectionId}
                                to={`#${sectionId}`}
                                onClick={(e) => {
                                    handleSmoothScroll(e, sectionId);
                                    handleClick(sectionId);
                                }}
                                className={getLinkClass(sectionId)}
                            >
                                {sectionId === 'home' 
                                    ? '∞' 
                                    : sectionId === '2019' || sectionId === '2018' || sectionId === '2015'
                                    ? sectionId
                                    : sectionId.charAt(0).toUpperCase() + sectionId.slice(1)}
                            </Link>
                        ))
                    ) : config.routes ? (
                        // Render route links (for Explore pages)
                        config.routes.map((route) => (
                            <Link
                                key={route.path}
                                to={route.path}
                                className={getLinkClass(route.path.split('/').pop() || '')}
                            >
                                {route.label}
                            </Link>
                        ))
                    ) : null}
                </div>
            </div>

            {/* Email Capture - Desktop Only, always visible */}
            {/* <div className="hidden md:block mt-auto pt-6 overflow-hidden">
                <button
                    type="button"
                    className="sidebar-item text-gray-400 hover:text-black transition-colors duration-500 text-right mb-2"
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                    tabIndex={-1}
                    aria-hidden="true"
                >

                </button>
                <EmailCapture theme={theme} serviceConfig={activeEmailConfig} />
            </div> */}
        </nav>
    );
};

export default Sidebar;
